import { action, observable, runInAction } from 'mobx'
import { featureFlagsClient } from '@winnin/feature-flags'

import PROVIDERS, { providersWithTwitch, PUBLIC_PROVIDERS_OPTIONS } from 'consts/providers'
import datadog from 'utils/datadog'
import getCurrentWorkspace from 'utils/getCurrentWorkspace'
import getEnvFromLocation from 'utils/getEnvFromLocation'

import { FEATURE_FLAGS_LOCAL_STORAGE_KEY } from './feature-flags.consts'
import type { FeatureFlag, TextSetting, UserInfo } from './feature-flags.types'

const TOKEN = {
  production: process.env.REACT_APP_CONFIGCAT_SDK_KEY_PRODUCTION,
  development: process.env.REACT_APP_CONFIGCAT_SDK_KEY_DEVELOPMENT,
}

class FeatureFlagsStore {
  @observable featureFlagsValues: Record<string, unknown> = {}
  @observable remoteFeatureFlagsValues: Record<string, unknown> = {}
  @observable isFeatureFlagsValuesReady = false
  @observable isLoggerEnabled = false
  @observable userInfo: UserInfo = {}
  @observable providersOptions = PUBLIC_PROVIDERS_OPTIONS
  storageKey = FEATURE_FLAGS_LOCAL_STORAGE_KEY

  private workspaceId: string | undefined = undefined

  private getWorkspaceId() {
    if (!this.workspaceId) {
      this.workspaceId = getCurrentWorkspace()
    }
    return this.workspaceId
  }

  @action init = async () => {
    featureFlagsClient.subscribe(
      action(flags => {
        this.featureFlagsValues = flags
        this.remoteFeatureFlagsValues = featureFlagsClient.getRemoteSnapshot()
        this.setFeatureFlagProviders()
      })
    )

    try {
      await featureFlagsClient.init({
        token: this.envToken(),
        getUser: () => {
          const { id, email, company, releaseType } = this.userInfo
          return {
            identifier: id,
            email,
            custom: {
              companyName: company?.name,
              companyId: company?.id,
              workspaceId: this.getWorkspaceId(),
              releaseType,
            },
          }
        },
        onEvaluate: (flag, value) => datadog.addFeatureFlagEvaluation(flag, value),
        localOverridesStorageKey: this.storageKey,
      })
    } finally {
      runInAction(() => {
        this.isFeatureFlagsValuesReady = true
      })
    }
  }

  @action setUserAndGetFlags = async (user: UserInfo) => {
    this.userInfo = user
    await this.refreshFlags()
  }

  @action refreshFlags = async () => {
    await featureFlagsClient.refreshFlags()
  }

  /** Optimistically updates a single flag value in the observable so all @observer
   * components re-render immediately, before the async ConfigCat fetch resolves. */
  @action setFlagOverride = (flag: FeatureFlag, value: boolean) => {
    this.featureFlagsValues[flag] = value
  }

  /** Optimistically restores flag values from the remote snapshot so components
   * re-render immediately when all overrides are cleared. */
  @action clearFlagOverrides = () => {
    this.featureFlagsValues = { ...this.remoteFeatureFlagsValues }
  }

  @action setFeatureFlagProviders() {
    const source = this.featureFlagsValues['istiktokenabled'] ? PROVIDERS : providersWithTwitch
    this.providersOptions = source.NAMES.map(v => ({
      value: v.KEY,
      text: source[v.KEY].NAME,
      label: source[v.KEY].NAME,
      color: source[v.KEY].COLOR,
    }))
  }

  // Reads from the MobX @observable — triggers re-renders in @observer components.
  // Also calls featureFlagsClient.isEnabled() as a side-effect so the onEvaluate hook
  // (Datadog RUM feature-flag tracking) still fires on every boolean flag read.
  isFeatureFlagEnabled(flag: FeatureFlag) {
    featureFlagsClient.isEnabled(flag)
    return !!this.featureFlagsValues[flag]
  }

  getFeatureFlagValueEvaluation(flag: TextSetting) {
    return featureFlagsClient.getValue(flag)
  }

  // Alias of isFeatureFlagEnabled for semantic clarity in @observer components
  getFeatureFlag(flag: FeatureFlag) {
    return this.isFeatureFlagEnabled(flag)
  }

  @action async toggleFeatureFlagLogEvents() {
    featureFlagsClient.toggleLogEvents()
    this.isLoggerEnabled = featureFlagsClient.isLogEnabled()
  }

  private envToken() {
    const envUrl = getEnvFromLocation()
    switch (envUrl) {
      case 'production':
      case 'staging':
        return TOKEN.production
      case 'local':
      case 'test':
      case 'development':
        return TOKEN.development
      default:
        throw new Error(`@FeatureFlagsStore >>> Invalid env name for configCat. ${envUrl}`)
    }
  }
}

export default new FeatureFlagsStore()
